import { createAction, createAsyncThunk } from '@reduxjs/toolkit';
import { getRecord, getRecordsByState, updateRecordState } from '../../db/odin-db-client';
import type { OdinDBRecordMutation, OdinDBRecordMutationState } from '../../db/odin-db-types';
import { onlineStatusService } from '../../services/online-status.service';
import { extractErrorMessage } from '../../utils/error-utils';
import { dbApi } from '../services/db-api';
import { recordApi } from '../services/record';
import type { AppDispatch, RootState } from '../store';
import type { SyncDetails } from './sync-transforms';
import { applySyncTransforms } from './sync-transforms';

/**
 * Action dispatched when the network comes back online.
 * Using createAction ensures type safety when dispatching or listening to this event.
 */
export const networkOnline = createAction('network/online');

/**
 * Maximum number of retry attempts before a mutation is moved to permanent error state.
 * After this many transient failures, the record requires manual intervention.
 */
const MAX_RETRY_COUNT = 5;

/**
 * Lock name used for synchronizing offline mutation processing across tabs.
 */
const SYNC_LOCK_NAME = 'odin-offline-sync';

type AppThunkConfig = {
  state: RootState;
  dispatch: AppDispatch;
};

/** Result of a sync operation with context for the caller */
type SyncResult = {
  status: 'success' | 'aborted' | 'retry' | 'error' | 'conflict' | 'skipped' | 'lock-failed';
  shouldInvalidateCache: boolean;
};

/**
 * An async thunk that syncs a specific offline record by ID.
 * Useful for manual retry or on-demand sync from the UI.
 *
 * Uses the Web Locks API to ensure only one sync process runs across all tabs.
 */
export const syncOfflineMutation = createAsyncThunk<void, number, AppThunkConfig>(
  'offline/syncRecord',
  async (recordId, { dispatch, signal }) => {
    if (!onlineStatusService.getStatus()) {
      throw new Error('Cannot sync record while offline');
    }

    if (!navigator.locks) {
      console.warn('Web Locks API not supported. Sync concurrency protection disabled.');
      return runSyncMutation(recordId, dispatch, signal);
    }

    await navigator.locks.request(SYNC_LOCK_NAME, { ifAvailable: true }, async (lock) => {
      if (!lock) {
        console.log('Sync already in progress in another tab. Skipping single record sync.');
        return;
      }

      // Re-fetch the record after acquiring the lock to ensure it's still in a syncable state
      await runSyncMutation(recordId, dispatch, signal);
    });
  }
);

async function runSyncMutation(recordId: number, dispatch: AppDispatch, signal: AbortSignal) {
  const mutation = await getRecord(recordId);
  if (!mutation) {
    throw new Error(`Record mutation not found for id: ${recordId}`);
  }

  if (mutation.metadata.state === 'synced' || mutation.metadata.state === 'processing') {
    return;
  }

  const result = await syncMutation(dispatch, mutation, signal);

  if (result.shouldInvalidateCache) {
    dispatch(dbApi.util.invalidateTags(['OfflineRecord']));
    dispatch(recordApi.util.invalidateTags([{ type: 'Record', id: 'LIST' }]));
  }
}

/**
 * An async thunk that processes pending offline mutations.
 *
 * It iterates through records marked as 'pending', attempts to sync them with the backend
 * via the `addRecord` endpoint, and updates their local state to 'processing', 'error',
 * or deletes them upon success.
 *
 * Uses the Web Locks API to ensure only one sync process runs across all tabs.
 */
export const processOfflineMutations = createAsyncThunk<void, void, AppThunkConfig>(
  'offline/processMutations',
  async (_, { dispatch, signal }) => {
    if (!navigator.locks) {
      console.warn('Web Locks API not supported. Sync concurrency protection disabled.');
      return runSyncLogic(dispatch, signal);
    }

    await navigator.locks.request(SYNC_LOCK_NAME, { ifAvailable: true }, async (lock) => {
      if (!lock) {
        console.log('Sync already in progress in another tab or thread. Skipping.');
        return;
      }
      await runSyncLogic(dispatch, signal);
    });
  }
);

async function runSyncLogic(dispatch: AppDispatch, signal: AbortSignal) {
  // Since we hold the exclusive lock, any record still in 'processing' state
  // must be from a previous session that crashed
  const stuckRecords = await getRecordsByState('processing');
  for (const record of stuckRecords) {
    await updateRecordState(record.id, 'pending');
  }

  // Now process all pending mutations
  const pendingMutations = await getRecordsByState('pending');
  let shouldInvalidateCache = false;

  for (const mutation of pendingMutations) {
    // Stop if offline or thunk is aborted
    if (!onlineStatusService.getStatus() || signal.aborted) {
      break;
    }

    const result = await syncMutation(dispatch, mutation, signal);
    shouldInvalidateCache = shouldInvalidateCache || result.shouldInvalidateCache;

    if (result.status === 'retry' || result.status === 'aborted') {
      // Stop processing the rest of the sync queue on network/transient errors or abort
      break;
    }
  }

  if (shouldInvalidateCache) {
    dispatch(dbApi.util.invalidateTags(['OfflineRecord']));
    dispatch(recordApi.util.invalidateTags([{ type: 'Record', id: 'LIST' }]));
  }
}

/**
 * Helper function to sync a single mutation.
 * Returns a result object indicating the sync status and whether caches should be invalidated.
 *
 * Uses optimistic locking to prevent race conditions when multiple processes
 * attempt to sync the same record.
 *
 * Implements retry limits to prevent infinite retry loops on persistent transient errors.
 */
async function syncMutation(
  dispatch: AppDispatch,
  mutation: OdinDBRecordMutation,
  signal: AbortSignal
): Promise<SyncResult> {
  const isCreate = mutation.metadata.operation === 'create';
  const isUpdate = mutation.metadata.operation === 'update';

  if (!isCreate && !isUpdate) {
    return { status: 'skipped', shouldInvalidateCache: false };
  }

  // Check retry count before attempting sync
  const currentRetryCount = mutation.metadata.retryCount ?? 0;
  if (currentRetryCount >= MAX_RETRY_COUNT) {
    await updateRecordState(mutation.id, {
      newState: 'error',
      expectedState: mutation.metadata.state as OdinDBRecordMutationState,
      error: `Maximum retry attempts (${MAX_RETRY_COUNT}) exceeded. Manual intervention required.`,
    });

    return { status: 'error', shouldInvalidateCache: true };
  }

  // Use optimistic locking: only transition to 'processing' if still in expected state
  const lockAcquired = await updateRecordState(mutation.id, {
    newState: 'processing',
    expectedState: mutation.metadata.state as OdinDBRecordMutationState,
  });

  if (!lockAcquired) {
    // Another process changed the state, skip this record
    return { status: 'lock-failed', shouldInvalidateCache: false };
  }

  // Check abort signal before starting the request
  if (signal.aborted) {
    await updateRecordState(mutation.id, {
      newState: 'pending',
      expectedState: 'processing',
    });

    return { status: 'aborted', shouldInvalidateCache: false };
  }

  // Apply sync transforms to convert local format to API-expected format
  const recordToSync = applySyncTransforms(mutation.record, mutation.syncDetails as SyncDetails);

  let dispatchRequest: { abort: () => void; unwrap: () => Promise<number> } | null = null;
  // set up abort signal
  const abortHandler = () => dispatchRequest?.abort();
  signal.addEventListener('abort', abortHandler);

  try {
    // Dispatch the API request
    if (isCreate) {
      const { id, ...recordToCreate } = recordToSync; // ensure no id is sent for create
      dispatchRequest = dispatch(recordApi.endpoints.addRecord.initiate({ body: recordToCreate }));
    } else {
      dispatchRequest = dispatch(
        // the 'patch' here is the full record, as we merge it in the indexed db client to ensure all consecutive patches are applied
        recordApi.endpoints.updateRecord.initiate({ id: recordToSync.id!, patch: recordToSync })
      );
    }

    await dispatchRequest.unwrap();

    await updateRecordState(mutation.id, {
      newState: 'synced',
      expectedState: 'processing',
    });

    return { status: 'success', shouldInvalidateCache: true };
  } catch (err: unknown) {
    return handleSyncError(mutation, err);
  } finally {
    signal.removeEventListener('abort', abortHandler);
  }
}

/**
 * Handles errors from sync attempts and updates record state accordingly.
 * Distinguishes between transient errors (network issues, server overload) that should retry,
 * and permanent errors that require user intervention.
 */
async function handleSyncError(mutation: OdinDBRecordMutation, err: unknown): Promise<SyncResult> {
  const error = err as { status?: string | number; name?: string; data?: { message?: string }; message?: string };

  const isNetworkError = error?.status === 'FETCH_ERROR' || error?.name === 'TypeError';
  const isTransientError = error?.status === 503 || error?.status === 504 || error?.status === 408;
  const isAbortError = error?.name === 'AbortError';
  const isConflictError = error?.status === 900;

  if (isNetworkError || isTransientError || isAbortError) {
    // Revert to 'pending' with incremented retry count so it gets picked up next time
    await updateRecordState(mutation.id, {
      newState: 'pending',
      expectedState: 'processing',
      incrementRetryCount: true,
    });

    return { status: 'retry', shouldInvalidateCache: false };
  } else if (isConflictError) {
    await updateRecordState(mutation.id, {
      newState: 'conflict',
      expectedState: 'processing',
      error: extractErrorMessage(error),
    });

    return { status: 'conflict', shouldInvalidateCache: true };
  } else {
    await updateRecordState(mutation.id, {
      newState: 'error',
      expectedState: 'processing',
      error: extractErrorMessage(error),
    });

    return { status: 'error', shouldInvalidateCache: true };
  }
}
